export const quotesArticles = [
    {
        id: 'quotes.overview',
        title: 'Quotes / Proposals Overview',
        category: 'Quotes',
        order: 1,
        route: '/quotes',
        summary: 'Create and manage client-facing quotes and proposals',
        keywords: ['quote', 'proposal', 'estimate', 'pricing', 'client'],
        content: `
The Quotes page lets you create client-facing proposals with line items, pricing, and markup. Quotes exist **before** a Job/BOQ — when accepted, they can be converted into a Job with a populated Bill of Quantities.

**Quote Lifecycle:**

1. **Draft** — Create and edit the quote (the only state in which lines and details change)
2. **Sent** — Emailed to the client with a secure view link
3. **Viewed** — The client opened the link
4. **Accepted / Rejected / Expired** — The client responded, or the Valid Until date passed
5. **Won / Lost** — Final outcome tracked (Won only after Accepted; Lost never from a Draft)

Quotes are numbered **QTE-0001, QTE-0002 …** in order of creation; revisions of the same quote share the number with a letter (Rev A, B, C).

**Key Features:**

- Rich line items: products, headings, subtotals, comments
- Cost vs price separation with markup/margin
- Drag-and-drop reorder
- Add items from catalogue with cost centre filtering
- Public client view (hash-based URL, no login required)
- PDF generation
- Revision system (A, B, C...)
- Templates for reuse
- Convert accepted quotes to Jobs + BOQ
    `,
        expandableSections: [
            {
                title: 'Who can do what',
                content: `
- **Viewer** — sees every quote, proposal and lead read-only; no buttons to create, send or change anything
- **Any other role** (estimator, manager, admin, owner) — creates, edits, sends, duplicates and revises quotes and proposals, adds lines from the catalogue, works leads
- **Owner, admin or manager** — records the outcome: **Accept**, **Reject**, **Won**, **Lost**, **Expire**; **Delete** a draft; **Convert to Job**; delete a lead or reassign its owner
- **Owner or admin** — extend a sent quote's **validity**, upload Word templates, connect or disconnect **DocuSign**

A **proposal / tender is accepted only by the client's signature** (secure link or DocuSign) — the internal Accept button refuses it, and so does a plain "Accept" on the link.
        `,
            },
            {
                title: 'Creating a Quote',
                content: `
1. Click **+ New Quote** in the toolbar
2. Select a **Client** (or create a new contact inline)
3. Optionally link to an existing **Job**
4. Enter **Description**, **Site Address**, and **Valid Until** date
5. Choose to start from a **Template** if available
6. Click **Create & Open** to enter the quote builder
        `,
            },
            {
                title: 'Quote Builder',
                content: `
The builder has two panels:

**Left — Line Items Table:**
- Add items via toolbar buttons (Item, Heading, Subtotal, Comment) or right-click context menu
- **Right-click** any row to insert above, add from catalogue, or delete
- **Drag rows** to reorder (grab the grip handle)
- **Double-click** cells to edit: description, quantity, unit cost, markup %, unit price
- Pricing logic: changing cost recalculates price via markup; changing price back-calculates markup

**Right — Details Sidebar:**
- Client info, site address, valid until
- Notes (visible to client), internal notes, cover letter, terms & conditions
- Deposit settings (percentage, fixed, or none)
- Pricing target (markup or margin mode with minimum threshold)
- History tab: created, sent, viewed, accepted dates
        `,
            },
            {
                title: 'Adding Items from Catalogue',
                content: `
Click **From Catalogue** in the toolbar or right-click → From Catalogue:

- **Cost Centre filter** — select one or more cost centres to narrow results
- **Search** — type to search by code or description
- **Shift+Click** to select a range of items
- **Select All** checkbox to add everything shown
- Click **Add Items** to insert them into the quote

Heading items (units with "Heading" in the name) are automatically added as section headings.
        `,
            },
            {
                title: 'Sending to Client',
                content: `
Click **Send** to preview the quote:

1. Review the client-facing preview (no cost/markup shown)
2. Enter or confirm the **email address**
3. Click **Send to Client** — sends branded email with a "View Quote" link
4. The client can **Accept** or **Decline** via the secure link (no login needed); a **proposal** is **signed** there instead
5. Status auto-updates: Sent → Viewed (on first open) → Accepted/Rejected
6. The link works until the **Valid Until** date (the whole of that day); a lapsed quote can no longer be accepted, declined or signed until an owner/admin extends the validity. With no Valid Until it stays open until you change it.

The address you type must be a real email — the quote's history records who sent it, when and to whom.
        `,
            },
            {
                title: 'Converting to Job',
                content: `
When a quote is accepted or won (an owner, admin or manager):

1. Click **Convert to Job** in the builder header
2. A new job is created with the next job number (or the linked job is used)
3. Product line items become BOQ bill items
4. **Cost price** (not sell price) is used for BOQ unit prices
5. Adhoc items (no catalogue entry) get catalogue records **AH-<job>-001…** created

The conversion is all-or-nothing: if any line cannot be written (for example the workspace has no cost centre), nothing is created and the line is named. A quote converts **once** — a second attempt is refused and the job link stays.
        `,
            },
            {
                title: 'Revisions & Templates',
                content: `
**Revisions:** Click **Revise** on a sent/rejected quote to create a new revision (B, C, D...) with the same quote number.

**Templates:** Check "Save as template" when creating, or use **Duplicate** to copy an existing quote. Templates appear in the "Start from Template" dropdown.
        `,
            },
        ],
        relatedArticles: ['boq.overview', 'orders.overview', 'contacts.overview'],
        screenshots: [
            {
                src: 'quotes/quotes-create.png',
                alt: 'New Quote dialog',
                caption: 'Starting a new quote',
            },
        ],
    },
    {
        id: 'quotes.builder',
        title: 'Building a Quote',
        category: 'Quotes',
        order: 2,
        route: '/quotes',
        summary: 'Edit line items, pricing, totals, and terms in the Quote Builder',
        keywords: ['quote builder', 'line items', 'markup', 'margin', 'catalogue', 'deposit', 'terms', 'send quote', 'pdf', 'revision'],
        content: `
The Quote Builder opens automatically after **Create & Open** on a new quote, or by clicking any quote in the Quotes list. It is a full-screen editor with the **line items table** on the left and a **details sidebar** (Details / Notes tabs) on the right.

The header shows the quote number, revision badge, and status badge. A quote is only editable while in **Draft** — after sending, lines and details become read-only and the header shows the status actions **your role allows** (see *Who can do what* in the Quotes overview). Viewers see everything read-only.

Everything saves as you go — there is no separate Save button.
    `,
        expandableSections: [
            {
                title: 'Header & Status Actions',
                content: `
The buttons in the header change with the quote's status:

- **Send** (Draft, with at least one item) — opens the send preview
- **Accept** / **Reject** (Sent or Viewed; owner, admin or manager) — record the client's response; Reject prompts for an optional reason (up to 2,000 characters). A **proposal** cannot be accepted here — the client signs it from the secure link or DocuSign
- **Won** (Accepted only) / **Lost** (Sent, Viewed, Accepted, Rejected or Expired — never a Draft; owner, admin or manager) — record the final outcome
- **Convert to Job** (Draft, Accepted, or Won; owner, admin or manager) — creates a Job with the quote items as BOQ bill items, once; a "Converted to Job" badge shows afterwards
- **Revise** (Sent, Viewed, Rejected, or Expired) — creates a new revision (B, C, D...) as a fresh draft without the previous revision's signature or send history

The **⋯** menu holds: **Set Markup for All Items** (draft only), **Download PDF**, **Duplicate**, and **Refresh**. **Delete** (draft only; owner, admin or manager) is on the Quotes list.
        `,
            },
            {
                title: 'Line Items',
                content: `
While in Draft, use the toolbar to add rows: **+ Item**, plus icon buttons for **Heading**, **Subtotal**, and **Comment**, and **From Catalogue** to pick catalogue items.

**Editing:**

- **Double-click** a cell to edit Description, Qty, Unit, Cost, Markup%, or Unit Price — **Enter** commits, **Escape** cancels
- Pricing recalculates automatically: change **Cost** or **Markup%** and Unit Price updates; change **Unit Price** and Markup% is back-calculated
- New product items start with markup pre-filled from your minimum pricing target
- The **Cost** column is internal — it is never shown to the client

**Reordering & inserting:**

- **Drag** the grip handle to reorder rows
- **Right-click** a row to Insert Above (Product / Item, Heading, Subtotal, Comment), add From Catalogue, or Delete Row
- Click the **×** at the end of a row to remove it

**Catalogue picker:** filter by one or more **Cost Centres**, search by price code or description, **Shift+Click** to select a range, or tick **Select All**, then click **Add Items**.
        `,
            },
            {
                title: 'Totals, Pricing Target & Details',
                content: `
The totals bar under the items shows **Subtotal**, **Cost**, **Margin** (with %), **GST**, **Deposit** (if set), and **Total**.

The **Details** tab in the sidebar holds:

- **Client** — click **Change** to reassign, search contacts, or **Create New Contact** inline
- **Description**, **Site Address**, **Valid Until**, **Tax Rate (%)** — new quotes start at your workspace's rate (Settings → Company → GST); change it on the quote for GST-free work. Tax and totals are held to the cent
- **Linked Job** — shown when the quote is linked to a job
- **Deposit** — None, Percentage, or Fixed plus a value
- **Pricing Target** — choose **Markup** (% on cost) or **Margin** (% of sell price) and a minimum %. The current figure shows green when on target; a warning bar appears when the quote falls below the minimum. These two settings are the only thing this panel saves — a proposal's pricing, a signature or a DocuSign envelope are never changed by it

**Set Markup for All Items** (in the ⋯ menu) applies one markup % to every product item and recalculates their unit prices in one step.
        `,
            },
            {
                title: 'Notes, Terms & Sending',
                content: `
The **Notes** tab holds four text areas plus history:

- **Notes (visible to client)** — printed on the quote
- **Internal Notes** — never shown to the client
- **Cover Letter** — shown at the top of the quote
- **Terms & Conditions** — shown at the bottom
- **History** — Created (and by whom — a quote made through the API records the key), Last Updated, Sent, Sent To and by whom, Viewed by Client, Accepted/Rejected, Won/Lost dates and reasons

**Sending:** click **Send** to open the preview — an exact client view with no cost or markup columns. Enter the client's email and click **Send to Client**, or leave the email blank and click **Mark as Sent** to update the status without emailing.
        `,
            },
        ],
        relatedArticles: ['quotes.overview', 'contacts.add', 'boq.overview'],
        screenshots: [
            {
                src: 'quotes/quote-builder.png',
                alt: 'Quote Builder with line items table and details sidebar',
                caption: 'The Quote Builder — line items on the left, details sidebar on the right',
            },
        ],
    },
    {
        id: 'quotes.tender',
        title: 'Proposals & Tenders (Proposal Builder)',
        category: 'Quotes',
        order: 3,
        summary: 'Issue a proposal or tender: base price from a home template plus variations',
        content: `
A **proposal** (call it a tender if you prefer — the printed title is yours to set) prices a base home design plus a schedule of variations — the client sees the base figure and each variation's description and amount, never your line items or costings.

Create one with **New Proposal** on the Quotes page. Proposals behave like quotes everywhere else: revisions (Rev A/B/C), sending with a secure client link, accept/decline tracking, and won/lost reporting.
    `,
        expandableSections: [
            {
                title: 'Pricing: Base Price + Variations',
                content: `
**Base Price** — pick a **template job** (your standard home design) and pull its estimate total with one click. Only the total is used; the BOQ stays internal. The price is frozen when pulled — later BOQ edits never move an existing tender. Pull again deliberately to refresh.

**Variations** — everything that adjusts the base, grouped the way tenders read (Site Costs, Upgrades, Selections & Allowances, Promotions). Each line has a price source:
- **Manual** — the estimator types the amount (site costs from soil tests live here)
- **Template delta** — the difference between two template jobs' totals, e.g. *Aspire 24 Premium* minus *Aspire 24 Base*: exact spec/inclusion-level pricing, correct for the model's quantities
- **Recipe × qty** — an assembly priced per quantity (e.g. raised ceilings per lineal metre); the code and quantity are recorded for traceability

Rows can print as a **priced amount**, or as *included*, *Note* or *by owner* — exactly the keywords real tenders use. Credits print accounting-style: (150).

Totals are layered the way tenders read: Base + Site Costs + Statutory/BASIX = **Total House Base Price including Site Costs**, then + Client Variations + Provisional Allowances = **Total New Home Tender**. Figures are GST-inclusive by default (an itemised ex-GST mode is available).
        `,
            },
            {
                title: 'Margins (internal only)',
                content: `
Every priced element can carry the builder's **cost** and a **markup %** — the base (the template pull lands in *base cost*), each variation, and a separate **credit markup** for deletions so you don't hand back full marked-up value. Costs and markups are internal: they never print and never reach the client link.

The margin bar shows live **Total Cost / Sell (ex GST) / Margin $ / Margin % / Markup %**, flags rows without a recorded cost (treated as zero margin, conservatively), and warns in red when the total margin falls below the **company minimum** — set the minimum right there with the save button. Saving the tender also records cost/margin on the quote for margin reporting.
        `,
            },
            {
                title: 'Document Sections',
                content: `
The tender document wraps the pricing schedule in your narrative sections: Cover Letter, Scope of Works, Inclusions and Exclusions print **before** the price; Allowances, Payment Schedule and Terms print **after**. Add, reorder or remove sections freely — empty sections are omitted.

Every section has a **rich text editor** — headings, bold/italic/underline, font family, font size and colour, highlight, bulleted and numbered lists, links, images and dividers — and prints exactly as formatted. Click the **expand** button on a section to edit it full-screen.

Set the **default wording for every section once** in **Settings → Estimating Setup → Proposals & Margins → Document sections** — edit the Cover Letter, Closing, Notes, Preliminary Agreement and Acceptance, add your own sections, and order them around the price schedule. Every new proposal loads these automatically; the estimator then amends any section in place here in the Document Sections tab (per-proposal edits don't change the company defaults).

Tick **Optional** on a library section (e.g. a Christmas promotion or a special offer) to keep it in the library **without** adding it to every proposal — the estimator then picks it per proposal from the **Add section** menu here in the Document Sections tab, alongside the "blank section" options.

**Merge fields** personalise the wording. Type **\`{{\`** anywhere in a section (or click the **\`{ }\`** button in the editor toolbar) to pick from a list — client name, contact person, site address, proposal number, base price, proposal total, and more. They're filled in with the proposal's real values when it's previewed, downloaded, or sent.

Your **company logo** (Settings → Branding) prints in the proposal header and shows on the client's secure link automatically.

**Preview PDF** renders the branded tender exactly as the client receives it. The client's view (PDF and the secure link) shows section text plus description-and-amount pricing only.
        `,
            },
            {
                title: 'Winning',
                content: `
Click **Send** in the Proposal builder to save and issue it — enter the client's email to send their secure link, or leave it blank to mark it sent and use **Copy link** to share it yourself. The client views it online and **signs** it there (or through DocuSign) — a proposal is a contract, so it is never click-accepted: not from the link, and not with the internal Accept button. The signature record (name, time, the document's hash, the signer's address) is kept on the proposal and cannot be edited or forged from the app. The link accepts a signature until the **Valid Until** date; an owner or admin can extend it.

As the design evolves, **New Revision** issues the next draft (Revision 1, 2, Final Tender) carrying everything across except the previous signature. On acceptance, convert to a job (owner, admin or manager), then use **BOQ → Copy from Job** with the same template the base price came from, and apply the variations to the live estimate.
        `,
            },
        ],
        keywords: ['tender', 'proposal', 'base price', 'variations', 'template job', 'inclusions', 'specification levels', 'site costs', 'upgrade', 'volume builder'],
        route: '/quotes',
        relatedArticles: ['quotes.overview', 'sales.overview', 'boq.copy-from-job'],
    },
];
